import { forwardRef, memo } from 'react';
import { Streamdown } from 'streamdown';

import {
  Box,
  ListItem,
  Thead,
  Tbody,
  Tfoot,
  Tr,
  Td,
  type BoxProps,
} from '@frontend/legacy-components';

import {
  ResponseTable,
  ResponseTh,
  ResponseHeading,
  ResponseParagraph,
  ResponseUnorderedList,
  ResponseOrderedList,
  ResponseLink,
} from './subcomponents';

export type ResponseProps = BoxProps & {
  children?: React.ReactNode;
  componentOverrides?: Partial<
    React.ComponentProps<typeof Streamdown>['components']
  >;
};
export const Response = memo(
  forwardRef<HTMLDivElement, ResponseProps>(
    ({ children, componentOverrides, ...props }, ref) => {
      const defaultComponents: React.ComponentProps<
        typeof Streamdown
      >['components'] = {
        table: ({ className, children, ...props }) => {
          return (
            <ResponseTable className={className} tableProps={props}>
              {children}
            </ResponseTable>
          );
        },
        thead: ({ className, children, ...props }) => {
          return (
            <Thead className={className} {...props}>
              {children}
            </Thead>
          );
        },
        tbody: ({ className, children, ...props }) => {
          return (
            <Tbody className={className} {...props}>
              {children}
            </Tbody>
          );
        },
        tr: ({ className, children, ...props }) => {
          return (
            <Tr className={className} {...props}>
              {children}
            </Tr>
          );
        },
        tfoot: ({ className, children, ...props }) => {
          return (
            <Tfoot className={className} {...props}>
              {children}
            </Tfoot>
          );
        },
        colgroup: ({ className, children, ...props }) => {
          return (
            <colgroup className={className} {...props}>
              {children}
            </colgroup>
          );
        },
        col: ({ className, children, ...props }) => {
          return (
            <col className={className} {...props}>
              {children}
            </col>
          );
        },
        th: ({ className, children, ...props }) => {
          return (
            <ResponseTh className={className} {...props}>
              {children}
            </ResponseTh>
          );
        },
        td: ({ className, children, ...props }) => {
          return (
            <Td className={className} {...props}>
              {children}
            </Td>
          );
        },
        h1: ({ className, children, ...props }) => {
          return (
            <ResponseHeading as="h1" className={className} {...props}>
              {children}
            </ResponseHeading>
          );
        },
        h2: ({ className, children, ...props }) => {
          return (
            <ResponseHeading as="h2" className={className} {...props}>
              {children}
            </ResponseHeading>
          );
        },
        h3: ({ className, children, ...props }) => {
          return (
            <ResponseHeading as="h3" className={className} {...props}>
              {children}
            </ResponseHeading>
          );
        },
        h4: ({ className, children, ...props }) => {
          return (
            <ResponseHeading as="h4" className={className} {...props}>
              {children}
            </ResponseHeading>
          );
        },
        h5: ({ className, children, ...props }) => {
          return (
            <ResponseHeading as="h5" className={className} {...props}>
              {children}
            </ResponseHeading>
          );
        },
        h6: ({ className, children, ...props }) => {
          return (
            <ResponseHeading as="h6" className={className} {...props}>
              {children}
            </ResponseHeading>
          );
        },
        p: ({ className, children, ...props }) => {
          return (
            <ResponseParagraph className={className} {...props}>
              {children}
            </ResponseParagraph>
          );
        },
        li: ({ className, children, ...props }) => {
          return (
            <ListItem className={className} {...props}>
              {children}
            </ListItem>
          );
        },
        ul: ({ className, children, ...props }) => {
          return (
            <ResponseUnorderedList className={className} {...props}>
              {children}
            </ResponseUnorderedList>
          );
        },
        ol: ({ className, children, ...props }) => {
          return (
            <ResponseOrderedList className={className} {...props}>
              {children}
            </ResponseOrderedList>
          );
        },
        a: ({ className, children, ...props }) => {
          return (
            <ResponseLink className={className} {...props}>
              {children}
            </ResponseLink>
          );
        },
        // code: ({ className, children, ...props }) => {
        //   const match = /language-(\w+)/.exec(className || '');
        //   return match != null && match[1] != null && children != null ? (
        //     <CodeBlock language={match[1]} code={children as string} />
        //   ) : null;
        // },
      };

      return (
        <Box
          className="response-container"
          ref={ref}
          display="flex"
          flexDirection="column"
          gap={1}
          sx={{
            '.streamdown': {
              display: 'contents',
            },
          }}
          {...props}
        >
          <Streamdown
            className="streamdown"
            components={{
              ...defaultComponents,
              ...componentOverrides,
            }}
          >
            {children as string}
          </Streamdown>
        </Box>
      );
    },
  ),
  (prevProps, nextProps) => prevProps.children === nextProps.children,
);
Response.displayName = 'Response';
